import { forwardRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { GridOverlay, DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { makeStyles } from '@mui/styles';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { useIsScrolledToBottom } from '@bymason/util-hooks';
import { LinearProgress } from '../../mui/LinearProgress';
import { MdPlaylistAdd, RiFilterFill } from '../Icons';
import { LoadingButton } from '../../mui/LoadingButton';
import { Fade } from '../../mui/Fade';
import { Container } from '../../mui/Container';
import { DataGridNoResultsOverlay } from '../DataGridNoResultsOverlay';

const DEFAULT_BOTTOM_SCROLL_THRESHOLD = 150;

export interface DataGridInfiniteScrollProps
  extends Omit<
    DataGridProProps,
    | 'autoHeight'
    | 'autoPageSize'
    | 'autoPageSize'
    | 'components'
    | 'disableColumnMenu'
    | 'disableColumnReorder'
    | 'disableColumnResize'
    | 'disableColumnSelector'
    | 'hideFooter'
    | 'hideFooterPagination'
    | 'onPageChange'
    | 'onPageSizeChange'
    | 'page'
    | 'pageSize'
    | 'pagination'
    | 'paginationMode'
    | 'rowsPerPageOptions'
  > {
  bottomScrollThreshold?: number;
  loadMoreButton?: boolean;
  loadMoreButtonText?: string;
  loadedRowCount: number;
  isScrolledToBottomOverride?: boolean;
  onLoadMoreData: () => void;
}

const useStyles = makeStyles((theme: MasonTheme) => ({
  root: {
    paddingBottom: theme.spacing(2),
  },
  loadMoreFooterContainer: {
    textAlign: 'center',
  },
  loadMoreButton: {
    minWidth: 120,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  loadingOverlayRoot: {
    bottom: theme.spacing(-1),
    position: 'absolute',
    width: '100%',
  },
  loadingOverlayHide: {
    display: 'none',
  },
}));

export default forwardRef<HTMLDivElement, DataGridInfiniteScrollProps>(function DataGridInfiniteScroll(
  {
    bottomScrollThreshold = DEFAULT_BOTTOM_SCROLL_THRESHOLD,
    className,
    loadMoreButton,
    loadMoreButtonText = 'Load more',
    loadedRowCount,
    loading,
    isScrolledToBottomOverride,
    onLoadMoreData,
    ...props
  }: DataGridInfiniteScrollProps,
  ref
) {
  const classes = useStyles();
  const [isScrolledToBottom] = useIsScrolledToBottom(bottomScrollThreshold);
  const [loadMoreData, setLoadMoreData] = useState(false);

  const LoadingOverlay = () => (
    <GridOverlay>
      <div className={clsx(classes.loadingOverlayRoot, !!loadMoreButton && classes.loadingOverlayHide)}>
        <LinearProgress />
      </div>
    </GridOverlay>
  );

  const LoadMoreFooter = () => (
    <Fade in={true}>
      <Container className={classes.loadMoreFooterContainer} maxWidth={false}>
        <LoadingButton
          className={classes.loadMoreButton}
          color='secondary'
          endIcon={<MdPlaylistAdd />}
          loading={loading}
          loadingPosition='end'
          onClick={() => {
            onLoadMoreData();
          }}
          variant='contained'
        >
          {loadMoreButtonText}
        </LoadingButton>
      </Container>
    </Fade>
  );

  useEffect(() => {
    if (loadMoreData) {
      setLoadMoreData(false);
      onLoadMoreData();
    }
  }, [loadMoreData, onLoadMoreData]);

  useEffect(() => {
    if (isScrolledToBottom || isScrolledToBottomOverride) {
      setLoadMoreData(true);
    }
  }, [isScrolledToBottom, isScrolledToBottomOverride]);

  return (
    <DataGridPro
      {...props}
      autoHeight
      className={clsx(classes.root, className)}
      components={{
        Footer: loadedRowCount && loadMoreButton ? LoadMoreFooter : undefined,
        LoadingOverlay: loadedRowCount ? LoadingOverlay : undefined,
        NoResultsOverlay: DataGridNoResultsOverlay,
        OpenFilterButtonIcon: RiFilterFill,
      }}
      disableColumnMenu
      disableColumnReorder
      disableColumnResize
      disableColumnSelector
      disableDensitySelector
      hideFooter={!loadMoreButton}
      loading={loading}
      pagination={false}
      ref={ref}
    />
  );
});
